/**
 * Widget — injects a floating webphone UI into the DOM.
 * Features: FAB button, incoming call modal with ringtone, active call panel
 * with timer/mute/audio level/hangup. Pure vanilla JS, zero frameworks.
 */

import { WIDGET_CSS } from './styles';
import { ICONS } from './icons';
import type { GrowhatsWebphone } from '../core';
import type { CallOffer, CallActive, CallStatus } from '../types';

/**
 * Make `el` draggable around the viewport. Persists position in localStorage
 * keyed by `storageKey` so it sticks across reloads. The element keeps its
 * default position until the user actually drags it; on first drag we switch
 * from the CSS-driven (`bottom`/`right`) anchor to absolute `top`/`left`.
 *
 * Excludes interactive descendants (buttons/inputs) so clicks still work.
 */
function makeDraggable(el: HTMLElement, storageKey: string): void {
  // Restore previous position if any.
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const { left, top } = JSON.parse(raw) as { left: number; top: number };
      if (Number.isFinite(left) && Number.isFinite(top)) {
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        el.style.right = 'auto';
        el.style.bottom = 'auto';
        el.style.transform = 'none';
      }
    }
  } catch { /* localStorage may be blocked — fall back to CSS defaults */ }

  el.style.cursor = el.style.cursor || 'grab';

  let dragging = false;
  let startX = 0, startY = 0;
  let startLeft = 0, startTop = 0;
  const DRAG_THRESHOLD = 4; // px — distinguish drag from click
  let moved = false;

  const onPointerDown = (e: PointerEvent) => {
    // Don't hijack clicks on interactive children.
    const target = e.target as HTMLElement;
    if (target.closest('button, input, textarea, a, select, [data-no-drag]')) return;
    dragging = true;
    moved = false;
    startX = e.clientX;
    startY = e.clientY;
    const rect = el.getBoundingClientRect();
    startLeft = rect.left;
    startTop = rect.top;
    // Switch from the bottom/right CSS anchor to free top/left.
    el.style.right = 'auto';
    el.style.bottom = 'auto';
    el.style.left = `${startLeft}px`;
    el.style.top = `${startTop}px`;
    // Override CSS-driven transforms (discreet centering, show animations).
    // Once the user drags, free positioning wins over the animated entry.
    el.style.transform = 'none';
    el.style.cursor = 'grabbing';
    el.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!dragging) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (!moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    moved = true;
    let nextLeft = startLeft + dx;
    let nextTop = startTop + dy;
    // Clamp to viewport so the element can't be lost off-screen.
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const maxLeft = window.innerWidth - w;
    const maxTop = window.innerHeight - h;
    nextLeft = Math.max(0, Math.min(maxLeft, nextLeft));
    nextTop = Math.max(0, Math.min(maxTop, nextTop));
    el.style.left = `${nextLeft}px`;
    el.style.top = `${nextTop}px`;
  };

  const onPointerUp = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    el.style.cursor = 'grab';
    try { el.releasePointerCapture(e.pointerId); } catch { /* */ }
    if (moved) {
      const rect = el.getBoundingClientRect();
      try {
        localStorage.setItem(storageKey, JSON.stringify({ left: rect.left, top: rect.top }));
      } catch { /* */ }
    }
  };

  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerup', onPointerUp);
  el.addEventListener('pointercancel', onPointerUp);
}

export function renderWidget(webphone: GrowhatsWebphone, container: HTMLElement): () => void {
  const host = document.createElement('div');
  host.className = 'gw-webphone-host';
  // Allow clicks to pass through the host shadow container (only widget elements capture)
  host.style.cssText = 'position: fixed; top: 0; left: 0; width: 0; height: 0; z-index: 99999; pointer-events: none;';
  container.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = WIDGET_CSS;
  shadow.appendChild(styleEl);

  const root = document.createElement('div');
  root.className = 'gw-webphone' + (webphone['_theme'] === 'light' ? ' gw-light' : '');
  shadow.appendChild(root);

  // State
  const showFab = webphone['_settings'].showWidgetButton !== false;
  const canMakeCalls = webphone['_canMakeCalls'] !== false;
  const canReceiveCalls = webphone['_canReceiveCalls'] !== false;
  const draggable = webphone['_draggable'] !== false;
  let isOpen = webphone['_widgetOpen'];
  let connected = false;
  let activeCall: CallActive | null = null;
  let offers: CallOffer[] = [];
  let inputValue = '';
  let durationTimer: ReturnType<typeof setInterval> | null = null;
  let displayDuration = 0;
  let callState: 'idle' | 'ringing' | 'active' | 'outgoing' = 'idle';
  let audioLevelRAF: number | null = null;
  let minimized = false;

  // ─── Tone player (shared AudioContext) ───
  // Earlier versions used `new AudioContext()` + `oscillator.stop()` per call,
  // but on some browsers oscillator.stop() doesn't immediately silence
  // scheduled gain ramps, and a freshly-orphaned context can keep producing
  // a faint residual tone for hundreds of ms — that was the "som fraco que
  // continua" symptom users heard during the active call. Using ONE shared
  // context for the widget's lifetime and connecting/disconnecting a master
  // gain node makes start/stop instantaneous and idempotent.
  let toneCtx: AudioContext | null = null;
  type Tone = {
    name: 'ring' | 'ringback';
    osc: OscillatorNode;
    gain: GainNode;
    interval: ReturnType<typeof setInterval> | null;
  };
  let currentRing: Tone | null = null;
  let currentRingback: Tone | null = null;

  function ensureToneCtx(): AudioContext | null {
    if (toneCtx && toneCtx.state !== 'closed') return toneCtx;
    try {
      toneCtx = new AudioContext();
      return toneCtx;
    } catch {
      return null;
    }
  }

  function startTone(name: 'ring' | 'ringback', freq: number, onMs: number, offMs: number, level: number): Tone | null {
    const ctx = ensureToneCtx();
    if (!ctx) return null;
    if (ctx.state === 'suspended') { try { ctx.resume(); } catch { /* */ } }
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.value = 0;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      const cadence = () => {
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(level, now);
        gain.gain.setValueAtTime(0, now + onMs / 1000);
      };
      cadence();
      const interval = setInterval(cadence, onMs + offMs);
      return { name, osc, gain, interval };
    } catch {
      return null;
    }
  }

  function stopTone(tone: Tone | null) {
    if (!tone) return;
    if (tone.interval) clearInterval(tone.interval);
    try {
      tone.gain.gain.cancelScheduledValues(toneCtx?.currentTime ?? 0);
      tone.gain.gain.value = 0;
    } catch { /* */ }
    try { tone.osc.stop(); } catch { /* */ }
    try { tone.osc.disconnect(); } catch { /* */ }
    try { tone.gain.disconnect(); } catch { /* */ }
  }

  function startRingtone() {
    if (currentRing) stopTone(currentRing);
    currentRing = startTone('ring', 440, 1000, 2000, 0.12);
  }

  function stopRingtone() {
    if (!currentRing) return;
    stopTone(currentRing);
    currentRing = null;
  }

  function startRingback() {
    if (currentRingback) stopTone(currentRingback);
    currentRingback = startTone('ringback', 425, 1000, 4000, 0.08);
  }

  function stopRingback() {
    if (!currentRingback) return;
    stopTone(currentRingback);
    currentRingback = null;
  }

  function destroyToneCtx() {
    stopRingtone();
    stopRingback();
    try { toneCtx?.close(); } catch { /* */ }
    toneCtx = null;
  }

  // ─── Inline call-panel flash (auto-fired on call lifecycle events) ───
  // Slides a slim banner into the top of whichever call panel is visible
  // (active or incoming). Auto-dismisses after 2.5s. Uses the same chrome
  // the call panel already has — feels native to the widget instead of a
  // floating toast somewhere else on the screen.
  type ToastKind = 'success' | 'info' | 'warning' | 'error';
  let activeFlashTimer: ReturnType<typeof setTimeout> | null = null;
  function showToast(kind: ToastKind, title: string, message?: string) {
    // Anchor: prefer active call panel, fall back to incoming modal.
    const anchor = (activePanel.classList.contains('gw-show') ? activePanel
      : incomingModal.classList.contains('gw-show') ? incomingModal
      : null);
    if (!anchor) return; // no visible panel → drop silently

    // Replace any pending flash so banners don't stack.
    if (activeFlashTimer) clearTimeout(activeFlashTimer);
    anchor.querySelectorAll('.gw-call-flash').forEach(n => n.remove());

    const flash = document.createElement('div');
    flash.className = `gw-call-flash gw-call-flash-${kind}`;
    flash.innerHTML = `
      <span class="gw-call-flash-icon">${kind === 'success' ? '✓' : kind === 'warning' ? '!' : kind === 'error' ? '✕' : 'i'}</span>
      <span class="gw-call-flash-text">
        <strong>${escapeHtml(title)}</strong>${message ? `<span> · ${escapeHtml(message)}</span>` : ''}
      </span>
    `;
    anchor.insertBefore(flash, anchor.firstChild);
    requestAnimationFrame(() => flash.classList.add('gw-call-flash-show'));
    activeFlashTimer = setTimeout(() => {
      flash.classList.remove('gw-call-flash-show');
      setTimeout(() => flash.remove(), 250);
      activeFlashTimer = null;
    }, 2500);
  }

  // ─── FAB (hidden when showWidgetButton: false) ───
  const fab = document.createElement('button');
  fab.className = 'gw-fab';
  fab.style.cssText = 'bottom: 20px; right: 20px;';
  fab.innerHTML = ICONS.phone;
  fab.onclick = () => {
    if (callState === 'idle') { isOpen = !isOpen; }
    update();
  };
  if (showFab) root.appendChild(fab);

  const badge = document.createElement('span');
  badge.className = 'gw-badge';
  badge.style.display = 'none';
  fab.appendChild(badge);

  // ─── Minimized pill (for discreet mode during active/ringing calls) ───
  const minimizedPill = document.createElement('button');
  minimizedPill.className = 'gw-minimized';
  minimizedPill.onclick = () => { minimized = false; update(); };
  root.appendChild(minimizedPill);

  // ─── Panel (dialer) ───
  const panel = document.createElement('div');
  panel.className = 'gw-panel';
  panel.style.cssText = 'bottom: 86px; right: 20px;';
  root.appendChild(panel);

  // ─── Incoming call modal ───
  const incomingModal = document.createElement('div');
  incomingModal.className = 'gw-incoming-modal';
  root.appendChild(incomingModal);

  // ─── Active call panel ───
  const activePanel = document.createElement('div');
  activePanel.className = 'gw-active-panel';
  root.appendChild(activePanel);

  // ─── Drag-and-drop (each surface gets its own persisted position) ───
  if (draggable) {
    if (showFab) makeDraggable(fab, 'gw-pos-fab');
    makeDraggable(panel, 'gw-pos-panel');
    makeDraggable(incomingModal, 'gw-pos-incoming');
    makeDraggable(activePanel, 'gw-pos-active');
    makeDraggable(minimizedPill, 'gw-pos-minimized');
  }

  function update() {
    // FAB state
    if (showFab) {
      fab.className = 'gw-fab' + (callState === 'ringing' ? ' gw-fab-ring' : callState === 'active' || callState === 'outgoing' ? ' gw-fab-active' : '');
      badge.style.display = offers.length > 0 ? 'flex' : 'none';
      badge.textContent = String(offers.length);
    }

    // Discreet mode: no FAB → modals center on screen
    const discreet = !showFab;
    incomingModal.classList.toggle('gw-discreet', discreet);
    activePanel.classList.toggle('gw-discreet', discreet);

    // Minimized pill (shows when call is ringing/active and user minimized)
    const showMinimized = minimized && (callState === 'ringing' || callState === 'active' || callState === 'outgoing');
    minimizedPill.classList.toggle('gw-show', showMinimized);
    minimizedPill.classList.toggle('gw-min-ring', callState === 'ringing');
    if (showMinimized) {
      const label = callState === 'ringing' ? 'Chamada recebida' : callState === 'outgoing' ? 'Chamando...' : 'Em chamada';
      const phone = callState === 'ringing' ? (offers[0]?.from || '') : (activeCall?.peer.phone || '');
      const timer = (callState === 'active' || callState === 'outgoing') ? formatTime(displayDuration) : '';
      minimizedPill.innerHTML = `<div class="gw-min-label">${label}</div><div class="gw-min-phone">${formatPhone(phone)}</div>${timer ? `<div class="gw-min-timer">${timer}</div>` : ''}`;
    }

    // Panels — hide when minimized
    panel.classList.toggle('gw-open', isOpen && callState === 'idle');
    incomingModal.classList.toggle('gw-show', callState === 'ringing' && !minimized);
    activePanel.classList.toggle('gw-show', (callState === 'active' || callState === 'outgoing') && !minimized);

    if (callState === 'idle' && isOpen) renderDialer();
    if (callState === 'ringing') renderIncoming();
    if (callState === 'active' || callState === 'outgoing') renderActive();
  }

  // ─── Dialer ───
  function renderDialer() {
    panel.innerHTML = '';

    const header = el('div', 'gw-header');
    const title = el('div', 'gw-header-title');
    const dot = el('span', 'gw-header-dot' + (connected ? ' gw-on' : ''));
    title.appendChild(dot);
    title.append(' GroWHats Phone');
    header.appendChild(title);
    const closeBtn = el('button', 'gw-header-close');
    closeBtn.innerHTML = ICONS.close;
    closeBtn.onclick = () => { isOpen = false; update(); };
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Outbound disabled — show a placeholder so the user understands why the
    // dialer is gone instead of seeing a broken empty panel.
    if (!canMakeCalls) {
      const blocked = el('div', 'gw-dialer');
      blocked.style.cssText = 'padding: 32px 18px; text-align: center; color: var(--text2); font-size: 13px; line-height: 1.5;';
      blocked.textContent = canReceiveCalls
        ? 'Este usuário só pode receber chamadas.'
        : 'Chamadas desativadas para este usuário.';
      panel.appendChild(blocked);
      return;
    }

    const dialer = el('div', 'gw-dialer');

    const inputRow = el('div', 'gw-input-row');
    const input = document.createElement('input');
    input.className = 'gw-input';
    input.type = 'tel';
    input.placeholder = 'Numero do telefone';
    input.value = inputValue;

    const callBtn = document.createElement('button');
    callBtn.className = 'gw-call-btn';
    callBtn.innerHTML = `${ICONS.phone} Ligar`;
    callBtn.disabled = inputValue.replace(/\D/g, '').length < 8;

    input.oninput = () => { inputValue = input.value; callBtn.disabled = inputValue.replace(/\D/g, '').length < 8; };
    inputRow.appendChild(input);

    const backBtn = el('button', 'gw-backspace');
    backBtn.textContent = '\u232B';
    backBtn.onclick = () => { inputValue = inputValue.slice(0, -1); input.value = inputValue; callBtn.disabled = inputValue.replace(/\D/g, '').length < 8; };
    inputRow.appendChild(backBtn);
    dialer.appendChild(inputRow);

    const pad = el('div', 'gw-dialpad');
    ['1','2','3','4','5','6','7','8','9','*','0','#'].forEach(k => {
      const btn = el('button', 'gw-key');
      btn.textContent = k;
      btn.onclick = () => { inputValue += k; input.value = inputValue; callBtn.disabled = inputValue.replace(/\D/g, '').length < 8; };
      pad.appendChild(btn);
    });
    dialer.appendChild(pad);

    callBtn.onclick = async () => {
      if (!inputValue) return;
      try {
        const call = await webphone.startCall(inputValue.replace(/\D/g, ''));
        activeCall = call as any;
        callState = 'outgoing';
        displayDuration = 0;
        startRingback();

        // Only start the duration counter when the peer actually accepts —
        // otherwise the user sees a running timer during the ringing phase,
        // which is confusing UX. onPeerAccept fires from the server's
        // call:accept webhook forwarded over Socket.io.
        call.onPeerAccept(() => {
          stopRingback();
          callState = 'active';
          startDurationTimer();
          update();
        });
        call.onPeerReject(() => { stopRingback(); });
        call.onUnanswered(() => { stopRingback(); });
        call.onEnd(() => { stopRingback(); });

        update();
      } catch (err: any) {
        stopRingback();
        console.error('[Webphone] Call failed:', err);
        const status = err?.status as number | undefined;
        const code = err?.code as string | undefined;
        let title = 'Falha ao chamar';
        let detail = err?.message || 'Tente novamente em alguns segundos';
        if (code === 'CALL_FAILED' || /no LID found/i.test(detail)) {
          title = 'Número não encontrado';
          detail = 'Verifique se o número tem WhatsApp e tente enviar uma mensagem primeiro';
        } else if (status === 403 || code === 'CALLING_NOT_ALLOWED') {
          title = 'Chamada bloqueada';
          detail = err?.message || 'Limite de chamadas atingido ou recurso desabilitado';
        } else if (status === 409 || code === 'SESSION_BUSY') {
          title = 'Canal ocupado';
          detail = 'Já existe uma chamada ativa nesta linha. Encerre antes de iniciar outra.';
        }
        showToast('error', title, detail);
        callState = 'idle';
        update();
      }
    };
    dialer.appendChild(callBtn);
    panel.appendChild(dialer);
  }

  // ─── Incoming call modal ───
  function renderIncoming() {
    const offer = offers[0];
    if (!offer) return;

    const canAccept = offer.answerable !== false;
    const label = canAccept ? 'Chamada recebida' : 'Preparando chamada...';
    const acceptDisabled = canAccept ? '' : ' disabled style="opacity:0.45;pointer-events:none"';

    incomingModal.innerHTML = `
      <button class="gw-minimize-btn" title="Minimizar">−</button>
      <div class="gw-inc-pulse"><svg width="28" height="28" viewBox="0 0 24 24" fill="#00a884"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z"/></svg></div>
      <div class="gw-inc-label">${label}</div>
      <div class="gw-inc-phone">${formatPhone(offer.from)}</div>
      <div class="gw-inc-actions">
        <button class="gw-inc-reject" title="Recusar"><svg width="24" height="24" viewBox="0 0 24 24" fill="#fff" style="transform:rotate(135deg)"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z"/></svg></button>
        <button class="gw-inc-accept" title="Atender"${acceptDisabled}><svg width="24" height="24" viewBox="0 0 24 24" fill="#fff"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z"/></svg></button>
      </div>
    `;

    incomingModal.querySelector('.gw-minimize-btn')!.addEventListener('click', (e) => {
      e.stopPropagation();
      minimized = true;
      update();
    });

    incomingModal.querySelector('.gw-inc-accept')!.addEventListener('click', async () => {
      if (!canAccept) return;
      stopRingtone();
      // Forcibly tear down any leftover state from a previous call. Without
      // these two lines the active panel briefly renders with the prior
      // activeCall + a non-zero displayDuration (looking like the new call
      // already has elapsed time) before the fresh acceptCall() resolves.
      stopDurationTimer();
      activeCall = null;

      const acceptBtn = incomingModal.querySelector('.gw-inc-accept') as HTMLElement;
      const rejectBtn = incomingModal.querySelector('.gw-inc-reject') as HTMLElement;
      if (acceptBtn) { acceptBtn.style.opacity = '0.5'; acceptBtn.style.pointerEvents = 'none'; }
      if (rejectBtn) { rejectBtn.style.display = 'none'; }
      const label = incomingModal.querySelector('.gw-inc-label');
      if (label) label.textContent = 'Conectando...';
      // Keep the incoming modal visible until the SDK emits `call:started`
      // (fired synchronously inside acceptCall). That handler sets
      // activeCall, callState='active' and starts the duration timer — at
      // which point the modal hides and the active panel renders cleanly
      // with displayDuration === 0 (shows "Conectando..." spinner first,
      // then ticks up to 0:01).

      try {
        await offer.accept();
        offers = offers.filter(o => o.call_id !== offer.call_id);
      } catch (err: any) {
        console.error('[Webphone] Accept failed:', err);
        callState = offers.length > 0 ? 'ringing' : 'idle';
        update();
      }
    });

    incomingModal.querySelector('.gw-inc-reject')!.addEventListener('click', async () => {
      stopRingtone();
      await offer.reject();
      offers = offers.filter(o => o.call_id !== offer.call_id);
      callState = offers.length > 0 ? 'ringing' : 'idle';
      update();
    });
  }

  // ─── Active call panel ───
  function renderActive() {
    const call = activeCall;
    if (!call) return;

    const isMuted = call.muted;
    const isRinging = callState === 'outgoing';
    const isConnecting = callState === 'active' && displayDuration === 0;
    const statusText = isRinging ? 'Chamando...' : isConnecting ? 'Conectando...' : 'Em chamada';

    // Visual distinction per state:
    //   • outgoing (ringing)    — three animated dots, no timer, no level bar
    //   • active, 0s elapsed    — spinner (waiting for first audio frame)
    //   • active, >0s           — live timer + mic level bar
    const centerSlot = isRinging
      ? '<div class="gw-act-ring-dots"><span></span><span></span><span></span></div>'
      : isConnecting
        ? '<div class="gw-spinner"></div>'
        : formatTime(displayDuration);

    activePanel.innerHTML = `
      <button class="gw-minimize-btn" title="Minimizar">−</button>
      <div class="gw-act-status ${isRinging ? 'gw-act-status-ring' : ''}">${statusText}</div>
      <div class="gw-act-phone">${call.peer.displayName || formatPhone(call.peer.phone)}</div>
      <div class="gw-act-timer">${centerSlot}</div>
      ${isRinging ? '' : '<div class="gw-act-level"><div class="gw-act-level-bar" id="gw-level-bar"></div></div>'}
      <div class="gw-act-actions">
        <button class="gw-act-btn gw-act-mute ${isMuted ? 'gw-muted' : ''}" title="${isMuted ? 'Desmutar' : 'Mutar'}">${isMuted ? ICONS.micOff : ICONS.mic}</button>
        <button class="gw-act-btn gw-act-hangup" title="Desligar">${ICONS.hangup}</button>
      </div>
    `;

    activePanel.querySelector('.gw-minimize-btn')!.addEventListener('click', (e) => {
      e.stopPropagation();
      minimized = true;
      update();
    });

    activePanel.querySelector('.gw-act-mute')!.addEventListener('click', () => {
      if (call.muted) call.unmute(); else call.mute();
      update();
    });

    activePanel.querySelector('.gw-act-hangup')!.addEventListener('click', async () => {
      await call.end();
      activeCall = null;
      callState = 'idle';
      stopDurationTimer();
      stopAudioLevel();
      stopRingback();
      stopRingtone();
      update();
    });

    startAudioLevel();
  }

  // ─── Audio level ───
  function startAudioLevel() {
    stopAudioLevel();
    const analyser = webphone.getAudioAnalyser();
    if (!analyser) return;
    const data = new Uint8Array(analyser.frequencyBinCount);
    function tick() {
      analyser!.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const pct = Math.min(100, Math.round((sum / data.length / 128) * 100));
      const bar = shadow.getElementById('gw-level-bar');
      if (bar) bar.style.width = pct + '%';
      audioLevelRAF = requestAnimationFrame(tick);
    }
    tick();
  }

  function stopAudioLevel() {
    if (audioLevelRAF) { cancelAnimationFrame(audioLevelRAF); audioLevelRAF = null; }
  }

  // ─── Duration timer ───
  function startDurationTimer() {
    displayDuration = 0;
    stopDurationTimer();
    durationTimer = setInterval(() => {
      const prev = displayDuration;
      displayDuration++;
      // Full re-render on the first tick so the status transitions from
      // "Conectando..."+spinner to "Em chamada"+timer+level-bar. For every
      // subsequent tick we only patch the timer's textContent (cheaper than a
      // full innerHTML rebuild, which would also reset the mic-level bar).
      if (prev === 0) {
        update();
      } else {
        const timerEl = shadow.querySelector('.gw-act-timer');
        if (timerEl) timerEl.textContent = formatTime(displayDuration);
      }
    }, 1000);
  }

  function stopDurationTimer() {
    if (durationTimer) { clearInterval(durationTimer); durationTimer = null; }
    displayDuration = 0;
  }

  // ─── Events ───
  webphone.on('connection:status', (c) => { connected = c; update(); });

  webphone.on('call:incoming', (offer) => {
    if (!offers.some((o) => o.call_id === offer.call_id)) offers.push(offer);
    callState = 'ringing';
    startRingtone();
    showToast('info', offer.answerable === false ? 'Chamada recebida (conectando...)' : 'Chamada recebida', formatPhone(offer.from));
    // Intentionally do NOT flip `isOpen = true` here. The incoming modal is
    // centered and floats independently, so forcing the main panel open was
    // redundant — and worse, it left `isOpen=true` behind, which meant the
    // dialer re-appeared if the call ended immediately after accept.
    update();
  });

  webphone.on('call:offer:ready', (offer) => {
    if (!offers.some((o) => o.call_id === offer.call_id)) offers.push(offer);
    callState = 'ringing';
    showToast('info', 'Pronto para atender', formatPhone(offer.from));
    update();
  });

  webphone.on('call:started', (call) => {
    activeCall = call;
    // For outgoing calls the core emits `call:started` the moment the POST to
    // the server succeeds — the peer hasn't actually picked up yet. Stay in
    // the "outgoing" state (which shows "Chamando..." + pulsating dots + no
    // timer) until the real peer-accept signal arrives. Incoming flips
    // straight to "active" because the user already accepted the offer.
    if (call.direction === 'OUTGOING') {
      callState = 'outgoing';
      displayDuration = 0;
      // Start ringback regardless of how the call was initiated (dialer click
      // or external `api.call.start()`). Stopping is handled by `call:status
      // ACTIVE` or `call:ended` handlers.
      startRingback();
    } else {
      callState = 'active';
      startDurationTimer();
      showToast('success', 'Chamada atendida', formatPhone(call.peer.phone));
    }
    update();
  });

  webphone.on('call:ended', (callId, reason) => {
    stopRingtone();
    stopRingback();
    const wasActive = !!(activeCall && activeCall.id === callId);
    const hadOffer = offers.some(o => o.call_id === callId);
    console.info('[Webphone][Calls] call:ended', { callId, reason, wasActive, hadOffer });

    // Always remove the offer NOW so the incoming modal can't stay stuck
    // showing a call that no longer exists. The 2.5s linger only made sense
    // for ACTIVE calls (where we want to flash "Chamada encerrada" on the
    // active panel). For ringing offers that never got picked up, the modal
    // should disappear immediately.
    offers = offers.filter(o => o.call_id !== callId);

    if (wasActive) {
      stopDurationTimer();
      stopAudioLevel();
    }

    if (reason === 'rejected') {
      showToast('warning', 'Chamada recusada', hadOffer ? 'Você recusou a chamada' : 'O outro lado recusou');
    } else if (reason === 'timeout' && hadOffer) {
      showToast('warning', 'Chamada não atendida', 'O tempo expirou');
    } else if (wasActive || hadOffer) {
      showToast('info', 'Chamada encerrada');
    }

    if (wasActive) {
      // Active call: keep panel visible for the flash, then collapse.
      setTimeout(() => {
        if (activeCall && activeCall.id === callId) {
          activeCall = null;
          callState = 'idle';
        }
        if (offers.length > 0) callState = 'ringing';
        else minimized = false;
        update();
      }, 2500);
    } else {
      // Pure offer (never accepted) — modal hides immediately.
      if (offers.length > 0) callState = 'ringing';
      else { callState = 'idle'; minimized = false; }
      update();
    }
  });

  webphone.on('call:status', (callId, status) => {
    if (!activeCall || activeCall.id !== callId) return;

    if (status === 'ENDED' || status === 'FAILED' || status === 'DISCONNECTED') {
      activeCall = null;
      callState = 'idle';
      stopDurationTimer();
      stopAudioLevel();
      stopRingback();
      update();
      return;
    }

    // Peer accepted an outgoing call — transition from "Chamando..." to
    // "Em chamada". The duration timer starts here because the call was
    // initiated via `api.call.start()` from outside the widget (not via the
    // internal dialer button), which means the peerAccept callback chain
    // wasn't wired in the dialer's onclick. Status events are the reliable
    // path for that transition.
    if (status === 'ACTIVE' && (callState === 'outgoing' || (callState === 'active' && displayDuration === 0))) {
      stopRingback();
      // Toast specifically for OUTGOING flow: peer just picked up.
      if (activeCall && activeCall.direction === 'OUTGOING') {
        showToast('success', 'Chamada atendida', formatPhone(activeCall.peer.phone));
      }
      if (callState !== 'active') callState = 'active';
      if (!durationTimer) startDurationTimer();
      update();
    }
  });

  // Another agent on this inbox answered the call — drop the offer so we stop ringing.
  /** Show a short "atendida por X" confirmation before hiding the modal. */
  function showTakenBanner(takenBy: { idExternal: string; name?: string | null } | undefined) {
    const label = (takenBy?.name && takenBy.name.trim()) || takenBy?.idExternal || 'outro atendente';
    incomingModal.classList.remove('gw-show');
    // Temporarily render a non-interactive "answered elsewhere" card centered
    // where the incoming modal was. Auto-dismisses after ~2s.
    incomingModal.innerHTML = `
      <div class="gw-inc-taken">
        <div class="gw-inc-taken-check">✓</div>
        <div class="gw-inc-taken-label">Chamada atendida</div>
        <div class="gw-inc-taken-by">por ${label}</div>
      </div>
    `;
    incomingModal.classList.add('gw-show', 'gw-taken');
    setTimeout(() => {
      incomingModal.classList.remove('gw-show', 'gw-taken');
      update();
    }, 2200);
  }

  webphone.on('call:taken', ({ callId, takenBy }) => {
    const wasRinging = offers.some(o => o.call_id === callId);
    offers = offers.filter(o => o.call_id !== callId);
    if (wasRinging) {
      stopRingtone();
      callState = offers.length > 0 ? 'ringing' : 'idle';
      showTakenBanner(takenBy);
      showToast('info', 'Chamada atendida', `Por ${(takenBy?.name && takenBy.name.trim()) || takenBy?.idExternal || 'outro atendente'}`);
    }
  });

  // Missed-call notification — separate from `call:ended` so the widget can
  // show a clear "Não atendida" badge with caller info instead of the generic
  // "encerrada". The server emits this whenever a call terminates without
  // ever being accepted (no-answer timeout, peer reject of our outgoing,
  // local cancel before peer answer, peer cancel before we answer, etc.).
  webphone.on('call:missed', ({ from, direction }) => {
    const label = direction === 'incoming' ? 'Chamada perdida' : 'Não atendida';
    showToast('warning', label, formatPhone(from));
  });

  // Our own accept lost the race — dismiss the offer with the same UX as call:taken.
  webphone.on('call:claim:denied', ({ callId, takenBy }) => {
    offers = offers.filter(o => o.call_id !== callId);
    stopRingtone();
    callState = offers.length > 0 ? 'ringing' : 'idle';
    showTakenBanner(takenBy);
    showToast('warning', 'Chamada já atendida', `Por ${(takenBy?.name && takenBy.name.trim()) || takenBy?.idExternal || 'outro atendente'}`);
  });

  update();

  return () => {
    stopDurationTimer();
    stopAudioLevel();
    destroyToneCtx();
    container.removeChild(host);
  };
}

// ─── Helpers ───

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  } as Record<string, string>)[c] ?? c);
}

function el(tag: string, cls: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = cls;
  return e;
}

function formatPhone(phone: string): string {
  if (!phone) return '';
  // WhatsApp Linked Identity — not a phone number. Show it condensed so the
  // user sees something meaningful instead of a 15-digit blob.
  const lower = phone.toLowerCase();
  if (lower.endsWith('@lid') || lower.startsWith('lid:')) {
    const id = phone.replace(/@lid$/i, '').replace(/^lid:/i, '');
    return `WhatsApp #${id.slice(-6)}`;
  }
  const d = phone.replace(/\D/g, '');
  // Bare LID (>=14 digits, no country code structure).
  if (d.length >= 14) return `WhatsApp #${d.slice(-6)}`;
  if (d.length === 13 && d.startsWith('55')) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  if (d.length === 12 && d.startsWith('55')) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`;
  if (d.length >= 10) return `+${d}`;
  return phone;
}

function formatTime(seconds: number): string {
  return `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
}
